import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import type { BrenvioCondition } from "./easyEmailHelpers";

type Props = {
  isOpen: boolean;
  toggle: () => void;
  /** Modo insert: cria novo bloco. Modo edit: atualiza condição existente. */
  mode?: "insert" | "edit";
  /** Condição inicial para pré-preencher no modo edit. */
  initialCondition?: BrenvioCondition | null;
  onInsert: (condition: BrenvioCondition) => void;
  /** Callback no modo edit (se omitido, usa onInsert). */
  onUpdate?: (condition: BrenvioCondition) => void;
};

/**
 * Modal para inserir bloco condicional no Easy Email.
 * Por quê modal (não só sidebar): configurar tag/campo/segmento sem custom Block panel.
 * F2 polish: aceita modo edit + initialCondition para atualizar condições existentes.
 */
const ConditionalBlockModal: React.FC<Props> = ({
  isOpen,
  toggle,
  mode = "insert",
  initialCondition = null,
  onInsert,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const [type, setType] = useState<"tag" | "field" | "segment">("tag");
  const [tagValues, setTagValues] = useState("vip");
  const [fieldSlug, setFieldSlug] = useState("");
  const [fieldValue, setFieldValue] = useState("");
  const [segmentId, setSegmentId] = useState("");

  // Preenche form ao abrir no modo edit com initialCondition.
  useEffect(() => {
    if (isOpen && mode === "edit" && initialCondition) {
      setType(initialCondition.type as "tag" | "field" | "segment");
      if (initialCondition.type === "tag") {
        setTagValues(
          Array.isArray(initialCondition.values)
            ? initialCondition.values.join(", ")
            : ""
        );
      } else if (initialCondition.type === "field") {
        setFieldSlug(initialCondition.slug || "");
        setFieldValue(initialCondition.value || "");
      } else if (initialCondition.type === "segment") {
        setSegmentId(String(initialCondition.id || ""));
      }
    }
  }, [isOpen, mode, initialCondition]);

  const canSubmit = useMemo(() => {
    if (type === "tag") {
      return tagValues
        .split(",")
        .map((s) => s.trim())
        .some(Boolean);
    }
    if (type === "field") {
      return fieldSlug.trim() !== "";
    }
    return Number(segmentId) > 0;
  }, [type, tagValues, fieldSlug, segmentId]);

  const submit = () => {
    let condition: BrenvioCondition;
    if (type === "tag") {
      condition = {
        type: "tag",
        op: "in",
        values: tagValues
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };
    } else if (type === "field") {
      condition = {
        type: "field",
        slug: fieldSlug.trim(),
        op: "eq",
        value: fieldValue,
      };
    } else {
      condition = { type: "segment", id: Number(segmentId) };
    }
    if (mode === "edit" && onUpdate) {
      onUpdate(condition);
    } else {
      onInsert(condition);
    }
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>
        {mode === "edit"
          ? t("campaigns.form.editor.conditionalEditTitle")
          : t("campaigns.form.editor.conditionalTitle")}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted small">
          {t("campaigns.form.editor.conditionalHint")}
        </p>
        <FormGroup>
          <Label>{t("campaigns.form.editor.conditionalType")}</Label>
          <Input
            type="select"
            value={type}
            onChange={(e) =>
              setType(e.target.value as "tag" | "field" | "segment")
            }
          >
            <option value="tag">
              {t("campaigns.form.editor.conditionalTypeTag")}
            </option>
            <option value="field">
              {t("campaigns.form.editor.conditionalTypeField")}
            </option>
            <option value="segment">
              {t("campaigns.form.editor.conditionalTypeSegment")}
            </option>
          </Input>
        </FormGroup>
        {type === "tag" ? (
          <FormGroup>
            <Label>{t("campaigns.form.editor.conditionalTags")}</Label>
            <Input
              value={tagValues}
              onChange={(e) => setTagValues(e.target.value)}
              placeholder="vip, premium"
            />
          </FormGroup>
        ) : null}
        {type === "field" ? (
          <>
            <FormGroup>
              <Label>{t("campaigns.form.editor.conditionalFieldSlug")}</Label>
              <Input
                value={fieldSlug}
                onChange={(e) => setFieldSlug(e.target.value)}
                placeholder="cidade"
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("campaigns.form.editor.conditionalFieldValue")}</Label>
              <Input
                value={fieldValue}
                onChange={(e) => setFieldValue(e.target.value)}
                placeholder="SP"
              />
            </FormGroup>
          </>
        ) : null}
        {type === "segment" ? (
          <FormGroup>
            <Label>{t("campaigns.form.editor.conditionalSegmentId")}</Label>
            <Input
              type="number"
              min={1}
              value={segmentId}
              onChange={(e) => setSegmentId(e.target.value)}
            />
          </FormGroup>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="light" type="button" onClick={toggle}>
          {t("sending.modals.cancel")}
        </Button>
        <Button
          color="primary"
          type="button"
          disabled={!canSubmit}
          onClick={submit}
        >
          {mode === "edit"
            ? t("campaigns.form.editor.conditionalUpdate")
            : t("campaigns.form.editor.conditionalInsert")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ConditionalBlockModal;
